import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PlusAuthenticationService } from '@karve.it/core';
import { SubSink } from 'subsink';

import { environment } from '../../environments/environment';
import { ColorSchemeService } from '../color-scheme.service';

/**
 * Session-storage key holding the timestamps (epoch ms) of recent redirects
 * to FusionAuth. Used to detect and break a redirect round-trip loop.
 */
const FA_BOUNCE_KEY = 'fa_redirect_bounces';
const FA_BOUNCE_WINDOW_MS = 60_000;
const FA_BOUNCE_MAX = 3;

/**
 * Fixed, human-safe messages for known FusionAuth callback error codes.
 *
 * `fa_error` itself is never rendered: it is only used as a flag (and
 * logged). The query param is attacker-writable — anyone can craft a link
 * to /api/auth/callback?error_description=<whatever> (or straight to
 * /auth/login?fa_error=<whatever>) and would otherwise get arbitrary text
 * shown in an alert box next to the app logo on our domain.
 */
const FA_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Sign-in was cancelled. Please try again.',
  NO_LOGIN_PERMISSION: 'Your account does not have permission to sign in. Please contact your administrator.',
  LOCKED_OUT: 'Your account is temporarily locked. Please wait a few minutes and try again.',
};
const FA_ERROR_MESSAGE_FALLBACK = 'Sign-in failed. Please try again.';

/**
 * Straight-redirect login page.
 *
 * The legacy email/password form that used to render here is removed:
 * FusionAuth's hosted page is the only login UI. This component
 *
 *  - restores a cookie-authenticated session in place (bootstrapAuth is
 *    deliberately allowed on this page) so an x-deauthenticated bounce with
 *    still-valid cookies returns the user to their pre-login URL without any
 *    FusionAuth round trip — as a full document load when the restored
 *    session belongs to a different user than the one this document was
 *    booted with (see below);
 *  - otherwise redirects straight to FusionAuth's authorize endpoint,
 *    carrying the pre-login URL in the OAuth2 `state` param so the backend
 *    callback returns the user where they were;
 *  - never auto-redirects when the callback reported `fa_error` (the backend
 *    cleared our cookies — redirecting again would loop with FA's SSO
 *    cookie), showing an error card with a manual retry instead;
 *  - bails out to the logged-out page when redirecting to FusionAuth more
 *    than FA_BOUNCE_MAX times within FA_BOUNCE_WINDOW_MS, bounding any
 *    deauth ⇄ FusionAuth round-trip loop.
 */
@Component({
  selector: 'app-auth-redirect',
  templateUrl: './auth-redirect.component.html',
  styleUrls: ['./auth-redirect.component.scss', './auth.styles.scss'],
})
export class AuthRedirectComponent implements OnInit, OnDestroy {

  subs = new SubSink();

  /** Set when the FusionAuth callback reported an error — stops auto-redirecting. */
  faError?: string;

  /** Fixed human-safe message shown for `faError` — never a raw query param. */
  faErrorMessage?: string;

  /** True while we have (imminently) redirected out to FusionAuth. */
  redirecting = false;

  constructor(
    private colorSchemeSvc: ColorSchemeService,
    private plusAuth: PlusAuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.colorSchemeSvc.setColorScheme('light');

    const error = this.route.snapshot.queryParamMap.get('fa_error');
    if (error) {
      // The backend callback clears our cookies and lands here with fa_error
      // when the authorization-code exchange failed. Auto-redirecting to FA
      // again would loop (FA's SSO cookie sends us straight back), so show
      // the error and require an explicit retry. The param acts only as a
      // flag: known backend/FA codes map to a fixed message, anything else
      // (including attacker-crafted text) gets the generic one — the raw
      // value is never rendered, only logged for debugging.
      console.warn('[Auth] FusionAuth sign-in failed:', error);
      this.faError = error;
      this.faErrorMessage = FA_ERROR_MESSAGES[error] || FA_ERROR_MESSAGE_FALLBACK;
      return;
    }

    this.plusAuth.bootstrapAuth().then(async (authenticated) => {
      if (!authenticated) {
        this.redirectToFA();
        return;
      }

      // Cookie-based restore: no FA round trip, straight back to work.
      sessionStorage.removeItem(FA_BOUNCE_KEY);

      // Logout no longer reloads the document, so an in-place restore must
      // not run for a different user: the previous user's NgRx store and
      // singleton caches would stay in memory underneath the new session
      // (e.g. the cookie jar was churned by another tab without this
      // document ever observing a deauthentication). Only restore in place
      // when it is the user this document booted with, or when this document
      // never had a session (nothing user-specific in memory yet).
      const restoredUserId = this.plusAuth.jwtPayload?.id;
      if (this.plusAuth.bootUserId !== undefined && this.plusAuth.bootUserId !== restoredUserId) {
        // Different user — full document load boots a clean application.
        this.plusAuth.navigateFullPage(this.plusAuth.consumePostLoginUrl());
        return;
      }

      // Same user: restore in place via SPA navigation. navigateAfterLogin
      // itself recovers a failed SPA navigation (guard cancel, stale lazy
      // chunk) with a full document load, so its resolve value needs no
      // handling here.
      await this.plusAuth.navigateAfterLogin();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Redirect to FusionAuth's hosted login page. The pre-login URL rides in
   * the OAuth2 `state` param; the backend /api/auth/callback redirects back
   * to it after minting fresh cookies.
   */
  redirectToFA(): void {
    const authUrl = environment.faAuthUrl;
    if (!authUrl) {
      console.error('[Auth] Login is not configured: missing auth URL.');
      this.faError = 'missing_auth_url';
      this.faErrorMessage = FA_ERROR_MESSAGE_FALLBACK;
      return;
    }

    if (this.hitBounceLimit()) {
      sessionStorage.removeItem(FA_BOUNCE_KEY);
      this.router.navigate(['/auth/logged-out']);
      return;
    }

    this.redirecting = true;
    window.location.href = this.buildFAAuthorizeUrl();
  }

  /**
   * Builds the FusionAuth authorize URL. The recorded pre-login destination
   * rides in the OAuth2 `state` param so the backend's callback redirects
   * the freshly-signed-in user to where they were; entries pointing at the
   * login page itself (or anything non-path-like) are dropped — the backend
   * callback applies the same rule to whatever we send — and default to
   * '/dashboard'. Split from redirectToFA so specs can assert on the URL
   * without actually navigating.
   */
  buildFAAuthorizeUrl(): string {
    let returnTo = '';
    try {
      returnTo = localStorage.getItem('pre_login_url') || '';
    } catch { returnTo = ''; }
    // Never point `state` back at a login path — that would loop (login page
    // redirects to FA, FA's SSO cookie sends us straight back to the
    // callback, which returns us to the login page again).
    if (returnTo.startsWith('/auth/login') || returnTo === '/login' || !returnTo.startsWith('/')) {
      returnTo = '';
    }
    const state = returnTo || '/dashboard';

    const params = new URLSearchParams({
      client_id: environment.faClientId,
      redirect_uri: environment.faRedirectUri,
      response_type: 'code',
      scope: 'openid offline_access email',
      state,
    });

    return `${environment.faAuthUrl}?${params.toString()}`;
  }

  /**
   * Records this redirect attempt and returns true when FusionAuth has
   * already bounced us back into this page FA_BOUNCE_MAX times within the
   * last FA_BOUNCE_WINDOW_MS — i.e. we are in a round-trip loop.
   */
  private hitBounceLimit(): boolean {
    const now = Date.now();
    let stamps: number[] = [];
    try {
      stamps = JSON.parse(sessionStorage.getItem(FA_BOUNCE_KEY) || '[]');
    } catch { stamps = []; }
    stamps = stamps.filter((t) => now - t < FA_BOUNCE_WINDOW_MS);
    if (stamps.length >= FA_BOUNCE_MAX) {
      return true;
    }
    stamps.push(now);
    sessionStorage.setItem(FA_BOUNCE_KEY, JSON.stringify(stamps));
    return false;
  }

}